import { useState } from 'react';
import { MdInfo, MdLock, MdVisibility, MdVisibilityOff, MdVpnKey } from 'react-icons/md';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { InputAdornment, TextField, Button, CircularProgress, Tooltip } from '@mui/material';
import { IconButton, Typography } from '@mui/material';
import { confirmResetPassword } from 'api/api';
import ErrorMessage from 'components/ErrorMessage';
import { useAuth } from 'contexts/auth';
import { ChangePasswordFormProps, ChangePasswordRequestDTO } from 'dtos/RecoverPasswordDTO';
import { useFormik } from 'formik';
import { changePasswordFormSchema } from 'shared/validators';
import {
  Container,
  AppHeader,
  AppLogo,
  CardLogin,
  LoginLogo,
  LoginCardHeader,
  LoginCardBody,
  LoginCardFooter,
  NeedsValidation,
} from 'styles/Styles';
import { AppError } from 'utils/AppError';
import { handleApiError } from 'utils/handleApiError';
import { getBackgroundImage, getLogo, getSlugFromURL } from 'utils/whiteLabel';

import { version } from '../../constants';

interface RecoverPasswordProps {
  headerColor?: string;
  btnColor?: string;
}

export default function ChangePassword(props: RecoverPasswordProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const { push } = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const resetToken = queryParams.get('resetToken');
  const context = useAuth();
  const PROJECT = getSlugFromURL();

  const initialValues: ChangePasswordFormProps = {
    password: '',
    passwordConfirmation: '',
  };

  const { values, errors, isSubmitting, setFieldValue, handleSubmit, setSubmitting } = useFormik({
    validationSchema: changePasswordFormSchema,
    initialValues,
    onSubmit: (values: ChangePasswordFormProps) => {
      handleSubmitChangePassword(values);
    },
    validateOnChange: false,
    validateOnBlur: false,
  });

  async function handleSubmitChangePassword(fields: ChangePasswordFormProps) {
    if (!resetToken) {
      toast.warn('Parâmetro resetToken não encontrado na url.');
      return;
    }

    try {
      const payload: ChangePasswordRequestDTO = {
        newPassword: fields.password,
        resetToken,
      };

      await confirmResetPassword(payload);

      toast.success('Senha redefinida com sucesso! Você será redirecionado para a tela de login.');

      setTimeout(() => {
        push('/login');
      }, 3000);
    } catch (err) {
      const error = err as AppError;

      handleApiError(error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Container
      style={{ backgroundImage: `url(${getBackgroundImage(context.selectedProject ?? PROJECT)})` }}
    >
      <AppHeader>
        <CardLogin>
          <LoginLogo>
            <AppLogo src={getLogo(context.selectedProject ?? PROJECT)} height="135" alt="logo" />
          </LoginLogo>
          <LoginCardHeader style={{ backgroundColor: props.headerColor ?? '#0071bc' }}>
            Criar nova Senha
          </LoginCardHeader>
          <LoginCardBody>
            <NeedsValidation
              onSubmit={handleSubmit}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                }
              }}
            >
              <div>
                <TextField
                  id="input-password"
                  fullWidth
                  required
                  value={values?.password}
                  error={!!errors?.password}
                  onChange={(e) => setFieldValue('password', e.target.value.replace(/\s/g, ''))}
                  label="Senha"
                  placeholder="Abc12345"
                  type={showPassword ? 'text' : 'password'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdVpnKey />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          style={{ cursor: 'default', margin: '0 5px' }}
                          title="Deve conter pelo menos uma letra maiúscula, uma letra minúscula, um número e entre 8 a 20 caracteres"
                        >
                          <div>
                            <MdInfo color="rgba(0, 0, 0, 0.54)" />
                          </div>
                        </Tooltip>
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <MdVisibility /> : <MdVisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {errors?.password && <ErrorMessage error={errors?.password} />}
              </div>

              <div>
                <TextField
                  id="input-password-confirmation"
                  fullWidth
                  required
                  value={values?.passwordConfirmation}
                  error={!!errors?.passwordConfirmation}
                  type={showPasswordConfirmation ? 'text' : 'password'}
                  label="Confirmar senha"
                  placeholder="Abc12345"
                  onChange={(e) =>
                    setFieldValue('passwordConfirmation', e.target.value.replace(/\s/g, ''))
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdLock />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                        >
                          {showPasswordConfirmation ? <MdVisibility /> : <MdVisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {errors?.passwordConfirmation && (
                  <ErrorMessage error={errors?.passwordConfirmation} />
                )}
              </div>

              <Button
                type="submit"
                color="primary"
                className="btn-login"
                variant="contained"
                disabled={isSubmitting}
              >
                {!isSubmitting ? 'ENVIAR' : <CircularProgress color="inherit" size={24} />}
              </Button>
            </NeedsValidation>
          </LoginCardBody>
          <LoginCardFooter>
            <div className="d-flex justify-content-center">
              <Link to="/">Fazer login</Link>
            </div>
            <div className="mt-3 d-flex justify-content-center">
              <Typography variant="caption" color="textSecondary">
                {`versão: ${version}`}
              </Typography>
            </div>
          </LoginCardFooter>
        </CardLogin>
      </AppHeader>
    </Container>
  );
}
